import React from 'react';
import { Chip } from './StatusChip';

// Trigger chips. Only "On submit" is executed by the server; rules saved with the
// retired "On save" / "On set status" triggers are shown as not running.
const TRIGGER_TONE = {
  'On submit': 'info',
  'On Submit': 'info',
};

/**
 * <AutomationRuleRow /> — forms automation list item.
 * Every automated change writes to the audit trail; `runs` is the inspectable counter.
 *
 * @param {ReactNode} desc        what the rule does
 * @param {ReactNode} [condition] the condition → action summary
 * @param {string}    [action]    trigger label ("On submit")
 * @param {ReactNode} [runs]      run counter
 * @param {boolean}   [inactive]  rule switched off — says so in text, not just by fading
 * @param {string}    [warning]   e.g. "Never runs — trigger retired"
 * @param {ReactNode} [actions]   buttons (view activity, toggle, delete)
 */
/* Rule text saved through a latin1 column lost its arrows: "A → B" came back as
   "A ? B". A real question mark follows a word ("OK? Is…"); a lost arrow sits
   between spaces — show those as arrows again. Only plain strings are touched. */
const restoreArrows = (v) => (typeof v === 'string' ? v.replace(/ \? /g, ' → ') : v);

const AutomationRuleRow = ({ desc, condition, action, runs, inactive = false, warning, actions }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', borderTop: '1px solid var(--cw-border-light, #f3f4f6)', flexWrap: 'wrap' }}>
    <div aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--cw-blue-tint, #EBF4FF)', color: 'var(--cw-blue, #1A80E5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <i className="fa-solid fa-bolt" />
    </div>
    <div style={{ flex: '1 1 200px', minWidth: 0, opacity: inactive ? 0.7 : 1 }}>
      <div style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', fontWeight: 600, color: 'var(--cw-text)' }}>{restoreArrows(desc)}</div>
      {condition && <div className="cw-meta" style={{ marginTop: 2, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-word' }}>{restoreArrows(condition)}</div>}
      {warning && <div style={{ marginTop: 4, fontSize: 'var(--cs-text-xs, 0.75rem)', color: 'var(--cw-warning-fg, #92400e)', fontWeight: 600 }}><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> {warning}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexShrink: 0, flexWrap: 'wrap' }}>
      {action && <Chip tone={TRIGGER_TONE[action] || 'neutral'} label={action} />}
      {inactive && <Chip tone="neutral" label="Off" />}
      {runs != null && <span className="cw-meta" style={{ whiteSpace: 'nowrap' }}>{runs}</span>}
      {actions}
    </div>
  </div>
);

export default AutomationRuleRow;
